import React, { useState, useEffect, useCallback } from "react";
import "./meeting.css";
import { toast } from "react-toastify";
import { addMeeting, getMyMeetings } from "../../service/project_service";
import { getProfile } from "../../service/auth_service";
import DataTable from "../../components/custom_data_table";
import CustomLoadingindicator from "../../components/custom_loading";
import { RotateCcw, Send } from "lucide-react";

const Meeting = () => {
    const [formData, setFormData] = useState({
        employeename: "",
        date: "",
        startTime: "",
        endTime: "",
        description: "",
    });

    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(false);

    const fetchMeetings = useCallback(async () => {
        try {
            const meetingsRes = await getMyMeetings();
            if (meetingsRes?.status === "success") {
                const formattedMeetings = (meetingsRes.data || []).map((meeting, index) => ({
                    ...meeting,
                    sno: index + 1,
                }));
                setMeetings(formattedMeetings);
            }
        } catch (error) {
            toast.error(error?.message || "Failed to fetch meetings");
        }
    }, []);

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const profileRes = await getProfile();
            if (profileRes?.status === "success" && profileRes.data) {
                setFormData((prev) => ({
                    ...prev,
                    employeename: profileRes.data.employeename || "",
                }));
            }
            await fetchMeetings();
        } catch (error) {
            toast.error(error?.message || "Failed to fetch profile");
        } finally {
            setLoading(false);
        }
    }, [fetchMeetings]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleReset = () => {
        setFormData((prev) => ({
            ...prev,
            date: "",
            startTime: "",
            endTime: "",
            description: "",
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitLoading(true);
            const response = await addMeeting({
                date: formData.date,
                startTime: formData.startTime,
                endTime: formData.endTime,
                description: formData.description,
            });

            if (response.status === "success") {
                toast.success(response.message || "Meeting added successfully");
                handleReset();
                setIsTableLoading(true);
                await fetchMeetings();
                setIsTableLoading(false);
            }
        } catch (error) {
            toast.error(error?.message || "Failed to add meeting");
        } finally {
            setSubmitLoading(false);
        }
    };

    const columns = [
        { key: "sno", label: "S.No" },
        { 
          key: "date", 
          label: "Meeting Date",
          render: (val) => new Date(val).toLocaleDateString('en-GB')
        },
        { 
          key: "startTime", 
          label: "Start Time",
          render: (val) => val
        },
        { 
          key: "endTime", 
          label: "End Time",
          render: (val) => val
        },
        { 
          key: "description", 
          label: "Focus Area / Agenda",
          render: (val) => val
        },
    ];

    if (loading) return <CustomLoadingindicator />;

    return (
        <div className="meetings-view-container">
            <h2 className="page-main-heading">Meeting Management</h2>

            <div className="meeting-form-card meeting-main-card" style={{ marginTop: "24px" }}>
                <form className="crm-form" onSubmit={handleSubmit}>
                    <div className="form-group-row triple-fields">
                        <div className="report-form-group">
                            <label>Employee Name</label>
                            <input 
                              type="text" 
                              value={formData.employeename} 
                              readOnly 
                              disabled 
                              className="readonly-input"
                            />
                        </div>
                        <div className="report-form-group">
                            <label>Meeting Date</label>
                            <input
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="report-form-group">
                            <label>Start Time</label>
                            <input
                                type="time"
                                name="startTime"
                                value={formData.startTime}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group-row triple-fields">
                        <div className="report-form-group">
                            <label>End Time</label>
                            <input
                                type="time"
                                name="endTime"
                                value={formData.endTime}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="report-form-group span-2">
                            <label>Agenda Overview</label>
                            <textarea
                                placeholder="Detail meeting focus/agenda..."
                                name="description"
                                rows="1"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                style={{ height: "42px", borderRadius: "10px", resize: "none" }}
                            />
                        </div>
                    </div>

                    <div className="form-actions-admin">
                        <button 
                          type="button" 
                          className="reset-btn-admin" 
                          onClick={handleReset}
                          disabled={submitLoading}
                        >
                          <RotateCcw size={18} />
                          Reset
                        </button>
                        <button 
                          type="submit" 
                          className="submit-btn-admin" 
                          disabled={submitLoading}
                        >
                            <Send size={18} />
                            {submitLoading ? "Scheduling..." : "Submit"}
                        </button>
                    </div>
                </form>
            </div>

            <div >
                <div className="table-header-section">
                  <div className="title-area">
                    <h3>My Scheduled Meetings</h3>
                  </div>
                </div>
                {isTableLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                        <CustomLoadingindicator />
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={meetings}
                        pagination={true}
                        searchPlaceholder="Search meetings..."
                    />
                )}
            </div>
        </div>
    );
};

export default Meeting;
